import type { AmplienceTopLevelNavigationCategory } from './types';
import type {
  FocusEvent,
  KeyboardEvent,
  MouseEvent as ReactMouseEvent,
  PointerEvent as ReactPointerEvent,
  ReactElement,
} from 'react';

import { useRouter } from 'next/compat/router';
import { memo, useCallback, useEffect, useRef, useState } from 'react';
import isEqual from 'react-fast-compare';

import { TopLevelNavigationCategory } from './components/TopLevelNavigationCategory';
import { TopLevelNavigationPanel } from './components/TopLevelNavigationPanel';
import { useTopLevelNavigationCategories } from './hooks/useTopLevelNavigationCategories';
import { S } from './TopLevelNavigation.styled';
import { useStaticContext } from '~/utilities/context/static/StaticContext';
import { useTopNavTracking } from '~/utilities/hooks/useTopNavTracking';

const PANEL_ID = 'top-level-navigation-panel';

type TopLevelNavigationProps = {
  categories?: AmplienceTopLevelNavigationCategory[];
};

const TopLevelNavigationComponent = ({
  categories: amplienceCategories,
}: TopLevelNavigationProps): ReactElement => {
  const { accessibilityLabels } = useStaticContext();
  const router = useRouter();
  const navigationRef = useRef<HTMLElement>(null);
  const [activeCategoryIndex, setActiveCategoryIndex] = useState<number | null>(null);
  const [open, setOpen] = useState(false);

  const categories = useTopLevelNavigationCategories(amplienceCategories);
  const { track } = useTopNavTracking();

  const triggerRef = useRef<HTMLElement | null>(null);
  const isKeyboardUserRef = useRef(false);
  // The last touch on a category link, recorded on pointerdown (before focus and click fire).
  const lastTouchRef = useRef<{ timestamp: number; wasOpen: boolean } | null>(null);

  const activeCategory = activeCategoryIndex === null ? undefined : categories[activeCategoryIndex];
  const columns = activeCategory?.columns ?? [];

  // Browsers move focus to a link on mouse or touch press, so focus alone does not mean
  // keyboard use: focus that directly follows a pointer press is attributed to the pointer.
  const lastPointerDownRef = useRef(0);

  const isPointerFocus = useCallback(() => Date.now() - lastPointerDownRef.current < 1000, []);

  const markKeyboardUser = useCallback(() => {
    isKeyboardUserRef.current = true;
    lastPointerDownRef.current = 0;
  }, []);

  const handleNavFocus = useCallback(() => {
    if (!isPointerFocus()) {
      isKeyboardUserRef.current = true;
    }
  }, [isPointerFocus]);

  const closeNavigation = useCallback(
    (returnFocusToTrigger = false) => {
      setOpen(false);

      if (returnFocusToTrigger) {
        requestAnimationFrame(() => {
          const trigger =
            triggerRef.current ||
            (activeCategoryIndex !== null
              ? document.getElementById(`top-level-navigation-toggle-${activeCategoryIndex}`)
              : null);

          trigger?.focus();
        });
      }
    },
    [activeCategoryIndex]
  );

  const isRecentTouch = useCallback(
    () => !!lastTouchRef.current && Date.now() - lastTouchRef.current.timestamp < 1000,
    []
  );

  const handleCategoryLinkPointerDown = useCallback(
    (index: number, event: ReactPointerEvent<HTMLAnchorElement>) => {
      lastTouchRef.current =
        event.pointerType === 'touch'
          ? { timestamp: Date.now(), wasOpen: open && activeCategoryIndex === index }
          : null;
    },
    [activeCategoryIndex, open]
  );

  const handleCategoryLinkFocus = useCallback(() => {
    if (isPointerFocus()) {
      return;
    }

    isKeyboardUserRef.current = true;
    setOpen(false);
  }, [isPointerFocus]);

  const trackedOpenIndexRef = useRef<number | null>(null);

  useEffect(() => {
    if (!open) {
      trackedOpenIndexRef.current = null;
    }
  }, [open]);

  const openCategory = useCallback(
    (index: number, triggerElement?: HTMLElement | null) => {
      if (trackedOpenIndexRef.current !== index) {
        trackedOpenIndexRef.current = index;
        track({
          event: 'header_interaction',
          element: 'nav_item',
          nav_item_label: categories[index].label,
          interaction_type: 'open',
        });
      }

      setActiveCategoryIndex(index);
      setOpen(true);
      triggerRef.current =
        triggerElement ||
        (typeof document !== 'undefined'
          ? document.getElementById(`top-level-navigation-toggle-${index}`) ||
            (document.activeElement as HTMLElement)
          : null);
    },
    [categories, track]
  );

  const handleCategoryLinkClick = useCallback(
    (index: number, event: ReactMouseEvent<HTMLAnchorElement>) => {
      const touch = isRecentTouch() ? lastTouchRef.current : null;

      lastTouchRef.current = null;

      // First tap on a category with a panel opens the panel; the second tap follows the link.
      if (touch && !touch.wasOpen && categories[index]?.columns?.length) {
        event.preventDefault();
        openCategory(index, event.currentTarget);

        return;
      }

      track({
        event: 'header_interaction',
        element: 'nav_item',
        nav_item_label: categories[index].label,
        interaction_type: 'click',
      });
    },
    [categories, isRecentTouch, openCategory, track]
  );

  const handleCategoryHover = useCallback(
    (index: number) => {
      // Decided on input modality alone: focus left inside the nav by a mouse click must not
      // block hover (the header stays mounted across client-side navigation).
      if (isKeyboardUserRef.current) {
        return;
      }

      if (categories[index]?.columns?.length) {
        openCategory(index);
      } else {
        closeNavigation();
      }
    },
    [categories, closeNavigation, openCategory]
  );

  const handleCategoryMouseMove = useCallback(
    (index: number) => {
      if (!open || activeCategoryIndex !== index) {
        handleCategoryHover(index);
      }
    },
    [activeCategoryIndex, handleCategoryHover, open]
  );

  // Keyboard mode only lasts while focus is inside the nav: once focus leaves, hover opens
  // panels again (e.g. after tabbing past the nav into a form).
  const handleBlur = useCallback((event: FocusEvent<HTMLElement>) => {
    if (!event.currentTarget.contains(event.relatedTarget)) {
      setOpen(false);
      isKeyboardUserRef.current = false;
    }
  }, []);

  // Keys pressed inside the nav mark keyboard use; keys elsewhere on the page (arrow-key
  // scrolling, tabbing through a form) must not disable hover. Escape is handled once, by the
  // window-level capture listener below.
  const handleKeyDown = markKeyboardUser;

  useEffect(() => {
    const handleGlobalKeyDown = (event: globalThis.KeyboardEvent) => {
      // Focus that follows a key press is keyboard focus, even shortly after a click.
      if (event.key === 'Tab') {
        lastPointerDownRef.current = 0;
      }

      if (event.key === 'Escape' && open) {
        event.preventDefault();
        event.stopPropagation();
        closeNavigation(true);
      }
    };

    // Capture phase, so this runs before the focus the press causes.
    const handleGlobalPointerDown = () => {
      isKeyboardUserRef.current = false;
      lastPointerDownRef.current = Date.now();
    };

    window.addEventListener('keydown', handleGlobalKeyDown, true);
    window.addEventListener('pointerdown', handleGlobalPointerDown, true);
    window.addEventListener('mousedown', handleGlobalPointerDown, true);

    return () => {
      window.removeEventListener('keydown', handleGlobalKeyDown, true);
      window.removeEventListener('pointerdown', handleGlobalPointerDown, true);
      window.removeEventListener('mousedown', handleGlobalPointerDown, true);
    };
  }, [closeNavigation, open]);

  useEffect(() => {
    if (!router) {
      return undefined;
    }

    const handleRouteChangeStart = () => {
      setOpen(false);
    };

    router.events.on('routeChangeStart', handleRouteChangeStart);

    return () => {
      router.events.off('routeChangeStart', handleRouteChangeStart);
    };
  }, [router]);

  useEffect(() => {
    if (!open) {
      return undefined;
    }

    const handleClickOutside = (event: MouseEvent | TouchEvent) => {
      if (navigationRef.current && !navigationRef.current.contains(event.target as Node)) {
        closeNavigation();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('touchstart', handleClickOutside);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
    };
  }, [closeNavigation, open]);

  const shouldFocusFirstLinkRef = useRef(false);

  const activateLinkWithSpace = useCallback((event: KeyboardEvent<HTMLAnchorElement>) => {
    if (event.key === ' ') {
      event.preventDefault();
      event.currentTarget.click();
    }
  }, []);

  const getFirstFocusableElement = useCallback(
    () =>
      navigationRef.current?.querySelector<HTMLElement>(
        `#${PANEL_ID} a[href], #${PANEL_ID} button:not([disabled])`
      ),
    []
  );

  const handleToggleClick = useCallback(
    (index: number, currentTarget?: HTMLElement) => {
      if (open && activeCategoryIndex === index) {
        closeNavigation(true);

        return;
      }

      shouldFocusFirstLinkRef.current = true;
      openCategory(index, currentTarget);
    },
    [activeCategoryIndex, closeNavigation, open, openCategory]
  );

  const handleToggleKeyDown = useCallback(
    (index: number, event: KeyboardEvent<HTMLButtonElement>) => {
      if (event.key === 'Enter' || event.key === ' ') {
        triggerRef.current = event.currentTarget;
        shouldFocusFirstLinkRef.current = true;
      }

      if (event.key === 'Tab' && !event.shiftKey && open && activeCategoryIndex === index) {
        const firstPanelLink = getFirstFocusableElement();

        if (firstPanelLink) {
          event.preventDefault();
          firstPanelLink.focus();
        }
      }
    },
    [activeCategoryIndex, getFirstFocusableElement, open]
  );

  useEffect(() => {
    if (open && shouldFocusFirstLinkRef.current) {
      shouldFocusFirstLinkRef.current = false;

      const focusTarget = () => {
        const firstFocusable = getFirstFocusableElement();

        if (firstFocusable) {
          firstFocusable.focus();
        } else {
          const panel = document.getElementById(PANEL_ID);

          panel?.focus();
        }
      };

      const animId = requestAnimationFrame(focusTarget);

      return () => {
        cancelAnimationFrame(animId);
      };
    }

    return undefined;
  }, [activeCategoryIndex, getFirstFocusableElement, open]);

  return (
    <S.Navigation
      ref={navigationRef}
      data-testid="top-level-navigation"
      aria-label={accessibilityLabels?.primaryNavigation ?? 'Primary navigation'}
      data-cs-capture=""
      onBlur={handleBlur}
      onFocus={handleNavFocus}
      onKeyDown={handleKeyDown}
      onMouseLeave={() => closeNavigation()}
    >
      <S.CategoryList>
        {categories.map((category, index) => {
          const hasOverlay = !!category.columns?.length;

          return (
            <TopLevelNavigationCategory
              key={category.id}
              category={category}
              index={index}
              isActive={open && activeCategoryIndex === index}
              isNavigationOpen={open}
              hasOverlay={hasOverlay}
              panelId={PANEL_ID}
              onMouseEnter={handleCategoryHover}
              onMouseMove={handleCategoryMouseMove}
              onClickLink={handleCategoryLinkClick}
              onFocusLink={handleCategoryLinkFocus}
              onKeyDownLink={activateLinkWithSpace}
              onPointerDownLink={handleCategoryLinkPointerDown}
              onClickToggle={handleToggleClick}
              onKeyDownToggle={handleToggleKeyDown}
            />
          );
        })}
      </S.CategoryList>

      <TopLevelNavigationPanel
        panelId={PANEL_ID}
        open={open}
        activeCategoryIndex={activeCategoryIndex}
        categoryLabel={activeCategory?.label ?? ''}
        columns={columns}
        closeNavigation={closeNavigation}
        activateLinkWithSpace={activateLinkWithSpace}
      />
    </S.Navigation>
  );
};

export const TopLevelNavigation = memo(TopLevelNavigationComponent, isEqual);
